import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import MedicalRecordsSystem, {
  MEDICAL_RECORD_CATEGORIES,
  MEDICAL_RECORD_TEMPLATES,
  EnhancedMedicalRecord,
  MedicalRecordCategory,
  MedicalRecordTemplate
} from '../lib/medicalRecordsSystem'
import MedicalDocumentUpload from './MedicalDocumentUpload'
import AllergyIntoleranceForm from './AllergyIntoleranceForm'
import PatientSelector from './PatientSelector'
import {
  Save,
  X,
  AlertCircle,
  CheckCircle,
  FileText,
  Calendar,
  Clock,
  User,
  Stethoscope,
  Brain,
  Pill,
  TestTube,
  Microscope,
  Activity,
  Shield,
  Plus,
  Minus,
  Eye,
  EyeOff,
  Lightbulb,
  Star,
  Tag,
  Upload
} from 'lucide-react'
import { toast } from 'sonner'

interface AdvancedMedicalFormProps {
  patientId?: string // Agora opcional para permitir seleção
  doctorId?: string
  recordId?: string // Para edição
  mode: 'create' | 'edit'
  onSave?: (record: EnhancedMedicalRecord) => void
  onCancel?: () => void
  initialData?: Partial<EnhancedMedicalRecord>
  allowPatientSelection?: boolean // Permitir seleção de paciente
  recordTypeFixed?: boolean // Tornar o tipo de registro não editável
}

interface FormField {
  name: string
  label: string
  type: 'text' | 'textarea' | 'select' | 'date' | 'number' | 'checkbox' | 'array' | 'object'
  required?: boolean
  placeholder?: string
  options?: { value: string; label: string }[]
  validation?: {
    minLength?: number
    maxLength?: number
    pattern?: string
    min?: number
    max?: number
  }
  help?: string
  defaultValue?: any
}

interface ValidationError {
  field: string
  message: string
}

const CATEGORY_ICONS = {
  consultation: Stethoscope,
  diagnosis: Brain,
  prescription: Pill,
  lab_result: TestTube,
  imaging: Microscope,
  surgery: Activity,
  vaccination: Shield
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baixa', color: 'text-gray-600' },
  { value: 'normal', label: 'Normal', color: 'text-blue-600' },
  { value: 'high', label: 'Alta', color: 'text-orange-600' },
  { value: 'critical', label: 'Crítica', color: 'text-red-600' }
]

export default function AdvancedMedicalForm({
  patientId,
  doctorId,
  recordId,
  mode,
  onSave,
  onCancel,
  initialData,
  allowPatientSelection = false,
  recordTypeFixed = false
}: AdvancedMedicalFormProps) {
  const { user } = useAuth()
  const [formData, setFormData] = useState<Partial<EnhancedMedicalRecord>>({
    patient_id: patientId,
    doctor_id: doctorId || user?.id || '',
    record_type: 'consultation',
    title: '',
    description: '',
    priority: 'normal',
    status: 'active',
    tags: [],
    custom_fields: {},
    ...initialData
  })
  const [rawTagsInput, setRawTagsInput] = useState<string>('')
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [saving, setSaving] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [dynamicFields, setDynamicFields] = useState<FormField[]>([])
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showDocumentUpload, setShowDocumentUpload] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState<any>(null)

  const generateDynamicFields = React.useCallback(() => {
    const category = MEDICAL_RECORD_CATEGORIES.find(c => c.id === formData.record_type)
    const template = selectedTemplate ? MEDICAL_RECORD_TEMPLATES.find(t => t.id === selectedTemplate) : null
    
    const fields: FormField[] = []

    // Campos básicos sempre presentes
    fields.push(
      {
        name: 'title',
        label: 'Título',
        type: 'text',
        required: true,
        placeholder: 'Título do registro médico',
        validation: { minLength: 5, maxLength: 200 },
        help: 'Título descritivo do registro médico'
      },
      {
        name: 'description',
        label: 'Descrição',
        type: 'textarea',
        placeholder: 'Descrição detalhada do registro',
        validation: { minLength: 10, maxLength: 2000 },
        help: 'Descrição completa do atendimento ou procedimento'
      }
    )

    // Campos específicos da categoria
    if (category) {
      Object.entries(category.template_fields).forEach(([fieldName, fieldType]) => {
        const field: FormField = {
          name: fieldName,
          label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          type: fieldType as any,
          placeholder: `Digite ${fieldName.replace(/_/g, ' ')}`
        }

        // Configurações específicas por tipo de campo
        switch (fieldName) {
          case 'vital_signs':
            field.type = 'object'
            field.help = 'Sinais vitais do paciente'
            break
          case 'medications':
            field.type = 'array'
            field.help = 'Lista de medicamentos prescritos'
            break
          case 'icd_codes':
            field.type = 'array'
            field.help = 'Códigos CID relacionados'
            break
          case 'test_date':
            field.type = 'date'
            break
          case 'dose_number':
            field.type = 'number'
            field.validation = { min: 1, max: 10 }
            break
        }

        fields.push(field)
      })
    }

    // Campos do template
    if (template) {
      Object.entries(template.fields).forEach(([fieldName, defaultValue]) => {
        if (!fields.find(f => f.name === fieldName)) {
          fields.push({
            name: fieldName,
            label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            type: typeof defaultValue === 'object' ? 'object' : 'text',
            defaultValue,
            placeholder: `Digite ${fieldName.replace(/_/g, ' ')}`
          })
        }
      })
    }

    // Campos avançados
    fields.push(
      {
        name: 'diagnosis',
        label: 'Diagnóstico',
        type: 'textarea',
        placeholder: 'Diagnóstico médico detalhado',
        help: 'Diagnóstico principal e secundários'
      },
      {
        name: 'treatment',
        label: 'Tratamento',
        type: 'textarea',
        placeholder: 'Plano de tratamento',
        help: 'Tratamento prescrito e recomendações'
      },
      {
        name: 'follow_up_date',
        label: 'Data de Retorno',
        type: 'date',
        help: 'Data para próxima consulta ou acompanhamento'
      },
      {
        name: 'reminder_date',
        label: 'Data de Lembrete',
        type: 'date',
        help: 'Data para lembrete automático'
      }
    )

    setDynamicFields(fields)
  }, [formData.record_type, selectedTemplate])

  const generateSuggestions = React.useCallback(() => {
    const category = MEDICAL_RECORD_CATEGORIES.find(c => c.id === formData.record_type)
    const suggestions: string[] = []

    switch (formData.record_type) {
      case 'consultation':
        suggestions.push(
          'Consulta de rotina',
          'Consulta de retorno',
          'Primeira consulta',
          'Consulta de urgência'
        )
        break
      case 'diagnosis':
        suggestions.push(
          'Diagnóstico confirmado',
          'Diagnóstico diferencial',
          'Diagnóstico provisório',
          'Reavaliação diagnóstica'
        )
        break
      case 'prescription':
        suggestions.push(
          'Prescrição inicial',
          'Renovação de receita',
          'Ajuste de medicação',
          'Prescrição de emergência'
        )
        break
      case 'lab_result':
        suggestions.push(
          'Exames de sangue',
          'Exames de urina',
          'Exames bioquímicos',
          'Exames hormonais'
        )
        break
    }

    setSuggestions(suggestions)
  }, [formData.record_type])

  // Função helper para remover tags duplicadas
  const removeDuplicateTags = (tags: string[]): string[] => {
    const seen = new Set<string>()
    return tags.filter(tag => {
      const lowerTag = tag.toLowerCase()
      if (seen.has(lowerTag)) {
        return false
      }
      seen.add(lowerTag)
      return true
    })
  }

  // Função para lidar com seleção de paciente
  const handlePatientSelect = (patient: any) => {
    setSelectedPatient(patient)
    setFormData({
      ...formData,
      patient_id: patient?.id || ''
    })
  }

  useEffect(() => {
    if (initialData) {
      // Remover duplicatas das tags iniciais se existirem
      const processedInitialData = { ...initialData }
      if (initialData.tags && initialData.tags.length > 0) {
        processedInitialData.tags = removeDuplicateTags(initialData.tags)
      }
      
      setFormData(prevData => ({ ...prevData, ...processedInitialData }))
      // Inicializar o input de tags com os dados existentes (sem duplicatas)
      if (processedInitialData.tags) {
        setRawTagsInput(processedInitialData.tags.join(', '))
      }
    }
  }, [initialData])

  useEffect(() => {
    generateDynamicFields()
    generateSuggestions()
  }, [generateDynamicFields, generateSuggestions])

  // Se for categoria de alergia, usar formulário específico
  if (formData.record_type === 'allergy_intolerance') {
    return (
      <AllergyIntoleranceForm
        patientId={patientId}
        doctorId={doctorId || user?.id || ''}
        recordId={recordId}
        mode={mode}
        initialData={initialData}
        onSave={onSave}
        onCancel={onCancel}
        allowPatientSelection={allowPatientSelection}
      />
    )
  }

  const validateForm = (): ValidationError[] => {
    const errors: ValidationError[] = []

    // Validações básicas
    if (!formData.title?.trim()) {
      errors.push({ field: 'title', message: 'Título é obrigatório' })
    } else if (formData.title.length < 5) {
      errors.push({ field: 'title', message: 'Título deve ter pelo menos 5 caracteres' })
    }

    // Validação obrigatória de paciente
    if (!formData.patient_id) {
      errors.push({ field: 'patient_id', message: 'Selecione um paciente para vincular o registro médico' })
    }

    if (!formData.doctor_id) {
      errors.push({ field: 'doctor_id', message: 'ID do médico é obrigatório' })
    }

    // Validações específicas da categoria
    const category = MEDICAL_RECORD_CATEGORIES.find(c => c.id === formData.record_type)
    if (category) {
      category.required_fields.forEach(field => {
        if (!formData[field as keyof EnhancedMedicalRecord]) {
          errors.push({ field, message: `Campo ${field} é obrigatório` })
        }
      })
    }

    // Validações de campos dinâmicos
    dynamicFields.forEach(field => {
      if (field.required && !formData[field.name as keyof EnhancedMedicalRecord]) {
        errors.push({ field: field.name, message: `${field.label} é obrigatório` })
      }

      const value = formData[field.name as keyof EnhancedMedicalRecord]
      if (value && field.validation) {
        if (field.validation.minLength && typeof value === 'string' && value.length < field.validation.minLength) {
          errors.push({ field: field.name, message: `${field.label} deve ter pelo menos ${field.validation.minLength} caracteres` })
        }
        if (field.validation.maxLength && typeof value === 'string' && value.length > field.validation.maxLength) {
          errors.push({ field: field.name, message: `${field.label} deve ter no máximo ${field.validation.maxLength} caracteres` })
        }
      }
    })

    return errors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const errors = validateForm()
    setValidationErrors(errors)
    
    if (errors.length > 0) {
      toast.error('Corrija os erros de validação')
      return
    }

    setSaving(true)

    try {
      let result
      
      if (mode === 'create') {
        result = await MedicalRecordsSystem.createRecord(formData)
      } else {
        result = await MedicalRecordsSystem.updateRecord(recordId!, formData)
      }

      if (result.error) {
        toast.error('Erro ao salvar registro médico')
        console.error(result.error)
        return
      }

      toast.success(`Registro médico ${mode === 'create' ? 'criado' : 'atualizado'} com sucesso`)
      onSave?.(result.data)
    } catch (error) {
      toast.error('Erro ao salvar registro médico')
      console.error(error)
    } finally {
      setSaving(false)
    }
  }

  const applyTemplate = (templateId: string) => {
    const template = MEDICAL_RECORD_TEMPLATES.find(t => t.id === templateId)
    if (!template) return

    const updatedData = MedicalRecordsSystem.applyTemplate(templateId, formData)
    setFormData(updatedData)
    setSelectedTemplate(templateId)
    toast.success('Template aplicado com sucesso')
  }

  const applySuggestion = (suggestion: string) => {
    setFormData({ ...formData, title: suggestion })
  }

  const addArrayItem = (fieldName: string) => {
    const currentArray = (formData[fieldName as keyof EnhancedMedicalRecord] as any[]) || []
    setFormData({
      ...formData,
      [fieldName]: [...currentArray, '']
    })
  }

  const removeArrayItem = (fieldName: string, index: number) => {
    const currentArray = (formData[fieldName as keyof EnhancedMedicalRecord] as any[]) || []
    const newArray = currentArray.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      [fieldName]: newArray
    })
  }

  const updateArrayItem = (fieldName: string, index: number, value: any) => {
    const currentArray = (formData[fieldName as keyof EnhancedMedicalRecord] as any[]) || []
    const newArray = [...currentArray]
    newArray[index] = value
    setFormData({
      ...formData,
      [fieldName]: newArray
    })
  }

  const getFieldError = (fieldName: string) => {
    return validationErrors.find(error => error.field === fieldName)
  }

  const availableTemplates = MEDICAL_RECORD_TEMPLATES.filter(
    t => t.category_id === formData.record_type && t.is_active
  )

  const CategoryIcon = CATEGORY_ICONS[formData.record_type as keyof typeof CATEGORY_ICONS] || FileText

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CategoryIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {mode === 'create' ? 'Novo Registro Médico' : 'Editar Registro Médico'}
                </h2>
                <p className="text-sm text-gray-600">
                  {MEDICAL_RECORD_CATEGORIES.find(c => c.id === formData.record_type)?.name}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {showAdvanced ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                <span>{showAdvanced ? 'Ocultar' : 'Mostrar'} Avançado</span>
              </button>
            </div>
          </div>

          {/* Seleção de categoria e template */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
              {recordTypeFixed ? (
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                  {MEDICAL_RECORD_CATEGORIES.find(cat => cat.id === formData.record_type)?.name || 'Consulta'}
                  <span className="text-xs text-gray-500 ml-2">(Fixo)</span>
                </div>
              ) : (
                <select
                  value={formData.record_type}
                  onChange={(e) => {
                    setFormData({ ...formData, record_type: e.target.value })
                    setSelectedTemplate('')
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {MEDICAL_RECORD_CATEGORIES.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
              <select
                value={selectedTemplate}
                onChange={(e) => e.target.value && applyTemplate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Nenhum template</option>
                {availableTemplates.map(template => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {PRIORITY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Sugestões */}
        {suggestions.length > 0 && (
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Lightbulb className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Sugestões de título:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => applySuggestion(suggestion)}
                  className="px-3 py-1 text-sm bg-white border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Erros de validação */}
        {validationErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-900">Erros de validação:</span>
            </div>
            <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index}>{error.message}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Seleção de Paciente */}
        {(allowPatientSelection || !patientId) && (
          <div className="bg-white rounded-lg shadow p-6">
            <PatientSelector
              selectedPatientId={formData.patient_id}
              onPatientSelect={handlePatientSelect}
              required={true}
              placeholder="Buscar paciente por nome ou email..."
            />
            {getFieldError('patient_id') && (
              <div className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                <AlertCircle className="h-4 w-4" />
                <span>{getFieldError('patient_id')?.message}</span>
              </div>
            )}
          </div>
        )}

        {/* Campos do formulário */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="space-y-4">
            {dynamicFields.map((field) => {
              const error = getFieldError(field.name)
              const value = formData[field.name as keyof EnhancedMedicalRecord]

              return (
                <div key={field.name} className={showAdvanced || ['title', 'description', 'diagnosis', 'treatment'].includes(field.name) ? '' : 'hidden'}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  
                  {field.type === 'textarea' ? (
                    <textarea
                      value={(value as string) || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      placeholder={field.placeholder}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        error ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      value={(value as string) || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        error ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Selecione...</option>
                      {field.options?.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  ) : field.type === 'array' ? (
                    <div className="space-y-2">
                      {((value as any[]) || []).map((item, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => updateArrayItem(field.name, index, e.target.value)}
                            placeholder={field.placeholder}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <button
                            type="button"
                            onClick={() => removeArrayItem(field.name, index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addArrayItem(field.name)}
                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Adicionar item</span>
                      </button>
                    </div>
                  ) : (
                    <input
                      type={field.type}
                      value={(value as string) || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      placeholder={field.placeholder}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        error ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  )}
                  
                  {field.help && (
                    <p className="text-xs text-gray-500 mt-1">{field.help}</p>
                  )}
                  
                  {error && (
                    <p className="text-xs text-red-600 mt-1">{error.message}</p>
                  )}
                </div>
              )
            })}

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <input
                type="text"
                value={rawTagsInput}
                placeholder="Digite tags separadas por vírgula"
                onChange={(e) => {
                  const inputValue = e.target.value
                  setRawTagsInput(inputValue)
                  
                  // Processar tags em tempo real quando houver vírgulas
                  if (inputValue.includes(',')) {
                    const tags = inputValue.split(',').map(tag => tag.trim()).filter(tag => tag)
                    const uniqueTags = removeDuplicateTags(tags)
                    setFormData({ ...formData, tags: uniqueTags })
                  } else {
                    // Se não há vírgulas, limpar as tags para mostrar apenas o que está sendo digitado
                    const currentTag = inputValue.trim()
                    if (currentTag) {
                      setFormData({ ...formData, tags: [currentTag] })
                    } else {
                      setFormData({ ...formData, tags: [] })
                    }
                  }
                }}
                onBlur={(e) => {
                  // Processar tags quando o usuário sair do campo
                  const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                  const uniqueTags = removeDuplicateTags(tags)
                  setFormData({ ...formData, tags: uniqueTags })
                  // Atualizar o input com as tags processadas
                  setRawTagsInput(uniqueTags.join(', '))
                }}
                onKeyDown={(e) => {
                  // Processar tags quando pressionar Enter
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    const tags = rawTagsInput.split(',').map(tag => tag.trim()).filter(tag => tag)
                    const uniqueTags = removeDuplicateTags(tags)
                    setFormData({ ...formData, tags: uniqueTags })
                    setRawTagsInput(uniqueTags.join(', '))
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {formData.tags && formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                      <Tag className="h-3 w-3 inline mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upload de documentos */}
        {showDocumentUpload && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Documentos Anexos</h3>
              <button
                type="button"
                onClick={() => setShowDocumentUpload(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <MedicalDocumentUpload
              patientId={patientId}
              recordId={recordId}
              onDocumentUploaded={(doc) => {
                toast.success('Documento anexado com sucesso')
              }}
            />
          </div>
        )}

        {/* Ações */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setShowDocumentUpload(!showDocumentUpload)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Upload className="h-4 w-4" />
                <span>Anexar Documentos</span>
              </button>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Salvando...' : 'Salvar Registro'}</span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}